import { useEffect, useState, useMemo } from "react";
import { MdEdit, MdDelete, MdAdd, MdSecurity, MdCheckCircle, MdSearch, MdKeyboardArrowRight, MdCategory, MdInfoOutline, MdHistory } from "react-icons/md";
import { useAuth } from "../../context/AuthContext";
import { useSearchParams } from "react-router-dom";
import { apiFetch } from "../../utils/api";
import { ClearFiltersButton } from "@euroasiann/shared/ui";

const authenticatedFetch = apiFetch;

export default function RolesPage() {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [roles, setRoles] = useState<any[]>([]);
  const [permissionsList, setPermissionsList] = useState<any[]>([]);
  const [roleName, setRoleName] = useState("");
  const [permissions, setPermissions] = useState<Record<string, boolean>>({});
  const [loading, setLoading] = useState(false);

  const [selectedRole, setSelectedRole] = useState<any>(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  // Initialize URL params from user data
  useEffect(() => {
    if (user?.organizationId) {
      const params = new URLSearchParams(searchParams);
      if (!params.get("portalType")) {
        params.set("portalType", "tech");
      }
      if (!params.get("organizationId")) {
        params.set("organizationId", user.organizationId);
      }
      setSearchParams(params, { replace: true });
    }
  }, [user?.organizationId]);

  // ⭐ 1️⃣ Load Tech Permissions
  const fetchPermissions = async () => {
    try {
      const res = await authenticatedFetch(`/api/v1/permissions?portalType=tech`);
      const json = await res.json();
      if (json.success) {
        setPermissionsList(json.data);
        const map: Record<string, boolean> = {};
        json.data.forEach((p: any) => (map[p.key] = false));
        setPermissions(map);
      }
    } catch (err) {
      console.error("Error fetching permissions:", err);
    }
  };

  // ⭐ 2️⃣ Load Roles
  const fetchRoles = async () => {
    if (permissionsList.length === 0) return;
    if (!user?.organizationId) return;

    setLoading(true);
    try {
      const params = new URLSearchParams({
        portalType: "tech",
        organizationId: user.organizationId,
      });
      const res = await authenticatedFetch(`/api/v1/roles?${params.toString()}`);
      const json = await res.json();

      if (!json.success) {
        console.error("Failed to fetch roles:", json.error);
        setLoading(false);
        return;
      }

      const mapped = json.data.map((r: any) => ({
        _id: r._id,
        portal: r.portalType,
        name: r.name,
        permissionsMap: permissionsList.reduce((acc: any, p: any) => {
          acc[p.key] = r.permissions?.includes(p.key) ?? false;
          return acc;
        }, {}),
      }));

      setRoles(mapped);
      if (mapped.length > 0 && !selectedRole) {
        setSelectedRole(mapped[0]);
      } else if (selectedRole) {
        const updated = mapped.find((r: any) => r._id === selectedRole._id);
        if (updated) setSelectedRole(updated);
      }
    } catch (err) {
      console.error("Error fetching roles:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPermissions();
  }, []);

  useEffect(() => {
    fetchRoles();
  }, [permissionsList]);

  // ⭐ Group permissions into categories
  const categorizedPermissions = useMemo(() => {
    const categories: Record<string, any[]> = {
      Overview: [],
      Deployment: [],
      Maintenance: [],
      System: [],
      Security: [],
      Other: []
    };

    permissionsList.forEach(p => {
      const key = p.key.toLowerCase();
      if (key.includes('dashboard') || key.includes('analytics')) categories.Overview.push(p);
      else if (key.includes('deploy') || key.includes('release')) categories.Deployment.push(p);
      else if (key.includes('service') || key.includes('maintenance') || key.includes('bug')) categories.Maintenance.push(p);
      else if (key.includes('system') || key.includes('config') || key.includes('org')) categories.System.push(p);
      else if (key.includes('security') || key.includes('audit')) categories.Security.push(p);
      else categories.Other.push(p);
    });

    return Object.fromEntries(Object.entries(categories).filter(([_, list]) => list.length > 0));
  }, [permissionsList]);

  // ⭐ Create Role
  const handleAddRole = async () => {
    if (!roleName.trim()) return;

    setIsCreating(true);
    const selectedPermissions = Object.keys(permissions).filter((p) => permissions[p]);

    try {
      const res = await authenticatedFetch(`/api/v1/roles`, {
        method: "POST",
        body: JSON.stringify({
          name: roleName,
          portalType: "tech",
          permissions: selectedPermissions,
        }),
      });

      if (!res.ok) {
        const error = await res.json();
        alert(error.error || "Failed to create role");
        setIsCreating(false);
        return;
      }

      const json = await res.json();
      if (json.success) {
        setRoleName("");
        setPermissions(Object.fromEntries(permissionsList.map(p => [p.key, false])));
        setIsEditMode(false);
        fetchRoles();
      }
    } catch (err: any) {
      console.error("Error creating role:", err);
      alert(err.message || "Failed to create role");
    } finally {
      setIsCreating(false);
    }
  };

  // ⭐ Save Edit
  const saveEdit = async () => {
    if (!selectedRole) return;
    setLoading(true);

    const selectedPermissions = permissionsList
      .filter((p) => selectedRole.permissionsMap[p.key])
      .map((p) => p.key);

    try {
      const res = await authenticatedFetch(`/api/v1/roles/${selectedRole._id}`, {
        method: "PUT",
        body: JSON.stringify({ permissions: selectedPermissions }),
      });

      if (!res.ok) {
        const error = await res.json();
        alert(error.error || "Failed to update role");
        return;
      }

      setIsEditMode(false);
      fetchRoles();
    } catch (err: any) {
      console.error("Error updating role:", err);
      alert(err.message || "Failed to update role");
    } finally {
      setLoading(false);
    }
  };

  // ⭐ Delete Role
  const handleDeleteRole = async (id: string) => {
    if (!window.confirm("Are you sure you want to delete this role?")) return;

    try {
      const res = await authenticatedFetch(`/api/v1/roles/${id}`, {
        method: "DELETE",
      });

      if (!res.ok) {
        const error = await res.json();
        alert(error.error || "Failed to delete role");
        return;
      }

      if (selectedRole?._id === id) setSelectedRole(null);
      fetchRoles();
    } catch (err: any) {
      console.error("Error deleting role:", err);
      alert(err.message || "Failed to delete role");
    }
  };

  // Filter roles
  const filteredRoles = roles.filter((r) =>
    r.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="flex flex-col h-[calc(100vh-73px)] bg-[hsl(var(--background))] overflow-hidden">
      {/* TOP HEADER */}
      <header className="flex-none p-6 border-b border-[hsl(var(--border))] bg-[hsl(var(--card))] sticky top-0 z-10">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mx-auto w-full">
          <div>
            <h1 className="text-[22px] font-bold text-[hsl(var(--foreground))] flex items-center gap-2">
              <MdSecurity className="w-6 h-6 text-[#0088cc]" />
              Roles & Permissions
            </h1>
            <p className="text-sm text-[hsl(var(--muted-foreground))] mt-1">
              Configure access levels and granular permissions for the organization.
            </p>
          </div>
          <div className="flex items-center gap-6">
            <div className="hidden md:flex flex-col items-end px-6 border-r border-[hsl(var(--border))]">
              <span className="text-xs font-bold uppercase tracking-wider text-[hsl(var(--muted-foreground))]">Global Capacity</span>
              <span className="text-xl font-bold text-[#0088cc] leading-none mt-1.5">{roles.length} Roles</span>
            </div>
            <button
              onClick={() => {
                setSelectedRole(null);
                setIsEditMode(true);
              }}
              className="flex items-center gap-2 px-4 py-2 bg-[#0088cc] text-white font-bold text-sm rounded hover:bg-[#006699] transition-colors"
            >
              <MdAdd className="w-4 h-4" />
              Create New Identity
            </button>
          </div>
        </div>
      </header>

      <main className="flex-1 flex overflow-hidden mx-auto w-full">
        {/* LEFT PANEL: ROLE LIST */}
        <aside className="w-80 md:w-96 flex-none border-r border-[hsl(var(--border))] flex flex-col bg-[hsl(var(--card))]">
          <div className="p-4 border-b border-[hsl(var(--border))] bg-[#0088cc]/5 space-y-3">
            <div className="relative group">
              <MdSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-[#0088cc]/60 group-focus-within:text-[#0088cc] transition-colors" />
              <input
                type="text"
                placeholder="Find role template..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 rounded border border-[#0088cc]/25 bg-[hsl(var(--card))] text-sm text-[hsl(var(--foreground))] focus:outline-none focus:border-[#0088cc] transition-colors"
              />
            </div>
            <ClearFiltersButton
              visible={searchQuery.trim() !== ''}
              onClick={() => setSearchQuery('')}
              variant="toolbar"
            />
          </div>

          <div className="flex-1 overflow-y-auto p-2 space-y-1">
            {loading && roles.length === 0 ? (
              Array.from({ length: 5 }).map((_, i) => (
                <div key={i} className="h-16 rounded bg-[#0088cc]/10 animate-pulse m-2" />
              ))
            ) : filteredRoles.length === 0 ? (
              <div className="flex flex-col items-center justify-center p-8 text-center text-[hsl(var(--muted-foreground))]">
                <MdInfoOutline className="w-10 h-10 mb-2 text-[#0088cc]/40" />
                <p className="text-sm font-semibold">No roles found</p>
              </div>
            ) : (
              filteredRoles.map((role) => (
                <button
                  key={role._id}
                  onClick={() => {
                    setSelectedRole(role);
                    setIsEditMode(false);
                  }}
                  className={`w-full flex items-center gap-3 p-3 rounded transition-colors group relative ${selectedRole?._id === role._id
                    ? "bg-[#0088cc]/10 border border-[#0088cc]/30"
                    : "hover:bg-[#0088cc]/5 border border-transparent"
                    }`}
                >
                  <div className={`p-2 rounded flex-shrink-0 ${selectedRole?._id === role._id ? "bg-[#0088cc] text-white" : "bg-[#0088cc]/10 text-[#0088cc]"
                    }`}>
                    <MdSecurity className="w-5 h-5" />
                  </div>
                  <div className="flex-1 text-left overflow-hidden">
                    <p className={`text-sm font-semibold truncate ${selectedRole?._id === role._id ? "text-[#0088cc]" : "text-[hsl(var(--foreground))]"}`}>{role.name}</p>
                    <p className="text-xs font-semibold text-[hsl(var(--muted-foreground))] truncate">
                      {Object.values(role.permissionsMap).filter(Boolean).length} permissions
                    </p>
                  </div>
                  <MdKeyboardArrowRight className={`w-5 h-5 text-[#0088cc]/50 transition-transform ${selectedRole?._id === role._id ? "translate-x-0 opacity-100" : "-translate-x-1 opacity-0 group-hover:opacity-100 group-hover:translate-x-0"}`} />
                </button>
              ))
            )}
          </div>
        </aside>

        {/* RIGHT PANEL: DETAILS/CREATE */}
        <section className="flex-1 overflow-y-auto bg-[hsl(var(--background))] p-6">
          {!selectedRole && !isEditMode ? (
            <div className="h-full flex flex-col items-center justify-center p-12 text-center bg-[hsl(var(--card))] rounded border border-[hsl(var(--border))]">
              <div className="w-16 h-16 rounded-full bg-[#0088cc]/10 flex items-center justify-center mb-4">
                <MdSecurity className="w-8 h-8 text-[#0088cc]" />
              </div>
              <h2 className="text-lg font-bold text-[hsl(var(--foreground))] mb-2">Select a role to view details</h2>
              <p className="text-sm font-semibold text-[hsl(var(--muted-foreground))] max-w-sm">
                Choose a role from the list on the left to manage its permissions or create a new one.
              </p>
            </div>
          ) : (
            <div className="max-w-4xl mx-auto p-8 space-y-8 bg-[hsl(var(--card))] rounded border border-[hsl(var(--border))]">
              {/* DETAIL HEADER */}
              <div className="flex items-start justify-between gap-6 pb-6 border-b border-[hsl(var(--border))]">
                <div className="flex-1">
                  {isEditMode && !selectedRole ? (
                    <div className="space-y-2">
                      <label className="text-xs font-bold text-[hsl(var(--muted-foreground))] uppercase tracking-wider">Creating New Role</label>
                      <input
                        autoFocus
                        value={roleName}
                        onChange={(e) => setRoleName(e.target.value)}
                        placeholder="Enter role name (e.g. System Admin)"
                        className="w-full text-2xl font-bold bg-transparent border-none outline-none placeholder:text-[hsl(var(--muted-foreground))]/50 p-0 focus:ring-0 text-[hsl(var(--foreground))]"
                      />
                    </div>
                  ) : (
                    <div>
                      <div className="flex items-center gap-3 mb-2">
                        <span className="px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-500/30">
                          Active Role
                        </span>
                        <span className="text-[hsl(var(--muted-foreground))] text-xs font-semibold flex items-center gap-1">
                          <MdHistory className="w-3 h-3" /> Updated recently
                        </span>
                      </div>
                      <h2 className="text-2xl font-bold text-[hsl(var(--foreground))]">
                        {selectedRole.name}
                      </h2>
                    </div>
                  )}
                </div>

                <div className="flex items-center gap-2">
                  {!isEditMode ? (
                    <>
                      <button
                        onClick={() => setIsEditMode(true)}
                        className="p-2 rounded bg-[#0088cc]/10 border border-[#0088cc]/25 text-[#0088cc] hover:bg-[#0088cc]/20 transition-colors"
                        title="Edit Role"
                      >
                        <MdEdit className="w-5 h-5" />
                      </button>
                      <button
                        onClick={() => handleDeleteRole(selectedRole._id)}
                        className="p-2 rounded bg-red-500/10 border border-red-500/30 text-red-600 dark:text-red-400 hover:bg-red-500/15 transition-colors"
                        title="Delete Role"
                      >
                        <MdDelete className="w-5 h-5" />
                      </button>
                    </>
                  ) : (
                    <>
                      <button
                        onClick={() => setIsEditMode(false)}
                        className="px-4 py-2 rounded border border-[hsl(var(--border))] bg-[hsl(var(--card))] text-[hsl(var(--muted-foreground))] hover:bg-[#0088cc]/5 text-sm font-bold transition-colors"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={selectedRole ? saveEdit : handleAddRole}
                        className="px-4 py-2 rounded bg-[#0088cc] text-white hover:bg-[#006699] text-sm font-bold transition-colors flex items-center gap-2"
                      >
                        {isCreating || loading ? (
                          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        ) : (
                          <MdCheckCircle className="w-4 h-4" />
                        )}
                        {selectedRole ? "Save Changes" : "Create Role"}
                      </button>
                    </>
                  )}
                </div>
              </div>

              {/* PERMISSIONS GRID */}
              <div className="space-y-8 pb-10">
                {Object.entries(categorizedPermissions).map(([category, perms]) => (
                  <div key={category} className="space-y-4">
                    <div className="flex items-center gap-3">
                      <div className="p-1 rounded bg-[#0088cc]/10">
                        <MdCategory className="w-4 h-4 text-[#0088cc]" />
                      </div>
                      <h3 className="text-sm font-bold text-[hsl(var(--foreground))]">{category}</h3>
                      <div className="flex-1 border-t border-[#0088cc]/15" />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {perms.map((perm) => (
                        <label
                          key={perm.key}
                          className={`flex items-start gap-3 p-3 rounded border transition-colors cursor-pointer group ${isEditMode
                            ? (selectedRole?.permissionsMap?.[perm.key] || permissions[perm.key])
                              ? "bg-[#0088cc]/10 border-[#0088cc]/30"
                              : "bg-[hsl(var(--card))] border-[#0088cc]/15 hover:border-[#0088cc]"
                            : (selectedRole?.permissionsMap?.[perm.key])
                              ? "bg-[#0088cc]/10 border-[#0088cc]/30"
                              : "bg-[#0088cc]/5 border-[#0088cc]/10 opacity-70 cursor-not-allowed"
                            }`}
                        >
                          <div className={`mt-0.5 relative flex-none w-5 h-5 rounded border transition-colors flex items-center justify-center ${(selectedRole?.permissionsMap?.[perm.key] || permissions[perm.key])
                            ? "bg-[#0088cc] border-[#0088cc]"
                            : "bg-[hsl(var(--card))] border-[#0088cc]/25 group-hover:border-[#0088cc]"
                            }`}>
                            <input
                              type="checkbox"
                              disabled={!isEditMode}
                              checked={!!(selectedRole ? selectedRole.permissionsMap[perm.key] : permissions[perm.key])}
                              onChange={() => {
                                if (selectedRole) {
                                  setSelectedRole({
                                    ...selectedRole,
                                    permissionsMap: {
                                      ...selectedRole.permissionsMap,
                                      [perm.key]: !selectedRole.permissionsMap[perm.key]
                                    }
                                  });
                                } else {
                                  setPermissions({
                                    ...permissions,
                                    [perm.key]: !permissions[perm.key]
                                  });
                                }
                              }}
                              className="absolute inset-0 opacity-0 cursor-pointer disabled:cursor-not-allowed"
                            />
                            {(selectedRole?.permissionsMap?.[perm.key] || permissions[perm.key]) && (
                              <MdCheckCircle className="w-3.5 h-3.5 text-white" />
                            )}
                          </div>
                          <div className="flex-1">
                            <p className="text-sm font-bold text-[hsl(var(--foreground))]">{perm.label}</p>
                            <p className="text-xs font-semibold text-[hsl(var(--muted-foreground))] mt-0.5 line-clamp-1">{perm.key}</p>
                          </div>
                        </label>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </section>
      </main>
    </div>
  );
}
